/**
 * LiveStreams Page
 * For supervisors to view RBT live streams
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { StreamViewer } from '../components/StreamViewer';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { StreamSession } from '../services/WebRTCService';
import { Video, Users, Clock, AlertCircle } from 'lucide-react';

const LiveStreams: React.FC = () => {
  const { user } = useAuth();
  const [activeStreams, setActiveStreams] = useState<StreamSession[]>([]);
  const [selectedStream, setSelectedStream] = useState<StreamSession | null>(null);
  const [loading, setLoading] = useState(true);

  // Listen for active streams for this supervisor
  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'streamSessions'),
      where('supervisorId', '==', user.id),
      where('status', 'in', ['waiting', 'live'])
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const streams: StreamSession[] = [];
      snapshot.forEach(doc => {
        streams.push({ id: doc.id, ...doc.data() } as StreamSession);
      });
      setActiveStreams(streams);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  if (!user || (user.role !== 'supervisor' && user.role !== 'admin')) {
    return (
      <div style={{ padding: '32px', textAlign: 'center' }}>
        <AlertCircle size={48} color="#dc2626" style={{ marginBottom: '16px' }} />
        <h2 style={{ color: '#dc2626', marginBottom: '8px' }}>Access Denied</h2>
        <p style={{ color: '#6b6b6b' }}>
          This page is only accessible to supervisors and admins.
        </p>
      </div>
    );
  }

  return (
    <>
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{
            fontSize: '28px',
            fontWeight: 'bold',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <Video color="#4F6367" />
            Live Streams
          </h1>
          <p style={{ color: '#6b6b6b' }}>
            Monitor your RBTs' sessions in real-time
          </p>
        </div>

        {/* Stats Bar */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '24px',
        }}>
          <div style={{
            backgroundColor: 'white',
            border: '1px solid #e5e0db',
            borderRadius: '8px',
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <div style={{
              backgroundColor: '#dbeafe',
              padding: '12px',
              borderRadius: '8px',
            }}>
              <Video size={24} color="#4F6367" />
            </div>
            <div>
              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {activeStreams.filter(s => s.status === 'live').length}
              </div>
              <div style={{ color: '#6b6b6b', fontSize: '14px' }}>Live Now</div>
            </div>
          </div>

          <div style={{
            backgroundColor: 'white',
            border: '1px solid #e5e0db',
            borderRadius: '8px',
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <div style={{
              backgroundColor: '#fef3c7',
              padding: '12px',
              borderRadius: '8px',
            }}>
              <Clock size={24} color="#d97706" />
            </div>
            <div>
              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {activeStreams.filter(s => s.status === 'waiting').length}
              </div>
              <div style={{ color: '#6b6b6b', fontSize: '14px' }}>Waiting</div>
            </div>
          </div>

          <div style={{
            backgroundColor: 'white',
            border: '1px solid #e5e0db',
            borderRadius: '8px',
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <div style={{
              backgroundColor: '#d1fae5',
              padding: '12px',
              borderRadius: '8px',
            }}>
              <Users size={24} color="#059669" />
            </div>
            <div>
              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {activeStreams.length}
              </div>
              <div style={{ color: '#6b6b6b', fontSize: '14px' }}>Total Active</div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: selectedStream ? '1fr 350px' : '1fr',
          gap: '24px',
        }}>
          {/* Stream Viewer */}
          <div style={{
            backgroundColor: 'white',
            border: '1px solid #e5e0db',
            borderRadius: '12px',
            overflow: 'hidden',
          }}>
            {loading ? (
              <div style={{
                padding: '64px',
                textAlign: 'center',
                color: '#6b6b6b',
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  border: '4px solid #e5e0db',
                  borderTop: '4px solid #4F6367',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 16px auto',
                }} />
                Loading streams...
              </div>
            ) : activeStreams.length === 0 ? (
              <div style={{
                padding: '64px',
                textAlign: 'center',
                color: '#6b6b6b',
              }}>
                <Video size={64} style={{ marginBottom: '16px', opacity: 0.3 }} />
                <h3 style={{ marginBottom: '8px', color: '#374151' }}>No Active Streams</h3>
                <p>When your RBTs start streaming, they'll appear here.</p>
              </div>
            ) : (
              <StreamViewer
                supervisorId={user.id}
                onStreamEnd={() => setSelectedStream(null)}
              />
            )}
          </div>

          {/* Stream List Sidebar (when a stream is selected) */}
          {selectedStream && (
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #e5e0db',
              borderRadius: '12px',
              padding: '16px',
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: 'bold',
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: '1px solid #e5e0db',
              }}>
                Stream Details
              </h3>

              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', color: '#6b6b6b', marginBottom: '4px' }}>
                  RBT
                </div>
                <div style={{ fontWeight: 'bold' }}>
                  {selectedStream.rbtName}
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', color: '#6b6b6b', marginBottom: '4px' }}>
                  Status
                </div>
                <span style={{
                  backgroundColor: selectedStream.status === 'live' ? '#d1fae5' : '#fef3c7',
                  color: selectedStream.status === 'live' ? '#059669' : '#d97706',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  textTransform: 'capitalize',
                }}>
                  {selectedStream.status}
                </span>
              </div>

              {selectedStream.startedAt && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '12px', color: '#6b6b6b', marginBottom: '4px' }}>
                    Started At
                  </div>
                  <div>
                    {selectedStream.startedAt.toDate().toLocaleTimeString()}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Active Streams Grid (when no stream is selected) */}
        {!selectedStream && activeStreams.length > 0 && (
          <div style={{ marginTop: '24px' }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 'bold',
              marginBottom: '16px',
            }}>
              All Active Streams
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '16px',
            }}>
              {activeStreams.map(stream => (
                <div
                  key={stream.id}
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e0db',
                    borderRadius: '8px',
                    padding: '16px',
                    cursor: 'pointer',
                    transition: 'box-shadow 0.2s',
                  }}
                  onClick={() => setSelectedStream(stream)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px',
                  }}>
                    <div style={{ fontWeight: 'bold' }}>{stream.rbtName}</div>
                    <span style={{
                      backgroundColor: stream.status === 'live' ? '#dc2626' : '#eab308',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      textTransform: 'uppercase',
                    }}>
                      {stream.status === 'live' ? '● LIVE' : 'WAITING'}
                    </span>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#6b6b6b',
                    fontSize: '14px',
                  }}>
                    <Clock size={14} />
                    Started {stream.createdAt?.toDate().toLocaleTimeString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* CSS for spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default LiveStreams;
